// Capacity, as the Claude Design draws it (screens2.jsx CapacityScreen,
// screens1.jsx HeatMap, domain.jsx TalentCard; owner, 2026-10-15: "exactly as
// it", nothing removed). Every figure is real or "—":
//   · tiles — utilisation and billable ratio from the weekly snapshots
//     (capacity_trend), bench hours from capacity_overview, freelance spend from
//     employee_compensation and only at people:full;
//   · the 12-week horizon — Skills × weeks (how booked the people holding each
//     skill are) or People × weeks (free hours), one Low…Over scale;
//   · talent cards — Talent's 4-week utilisation, level chips, "Free from".
// Leave is only ever "away", never its type.
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { can } from '../lib/roles';
import { useOrgDates } from '../lib/orgSettings';
import { listHolidays } from '../lib/holidays';
import { addDaysToKey, fmtDayKey } from '../lib/dates';
import { downloadCsv } from '../lib/csv';
import { fmtINR } from '../lib/money';
import { projectState } from '../lib/projects';
import {
  capacityCsvRows,
  fetchCapacity,
  fetchCapacityTrend,
  fmtDelta,
  freeFrom,
  hrs,
  skillLoad,
  suggestMix,
  utilisationByPerson,
  utilisationTone,
  weekBillableRatio,
  weekUtilisation,
  type AllocRow,
  type CapacityPerson,
  type CapacityTrendWeek,
  type CapacityWeek,
  type SkillReq,
} from '../lib/capacity';
import { Avatar, Badge, Button, Icon, Mono, Progress, Select } from '../components/primitives';
import { KpiGrid, KpiTile, SkillLevelChip } from '../components/domain';
import { Card, SegmentedControl } from '../components/card';
import { SectionHeader } from '../components/shell';
import { Dialog } from '../components/dialog';
import { EmptyState, LoadError } from '../components/feedback';
import { TextInput } from '../components/field';

const WEEKS = 12;
/** Talent's utilisation window: the four whole weeks before this one. */
const UTIL_WEEKS = 4;

// ── The design's Low…Over scale (screens1.jsx HeatMap `cell`) ───────────────
// Booked as a share of standard hours: grey, a whisper of sky, two blues, amber
// from 90%, rose past 100%. The design's colours, on the tokens.
const mix = (t: string, pct: number) => `color-mix(in srgb, var(${t}) ${pct}%, transparent)`;
const tint = (t: string, pct: number) => `color-mix(in srgb, var(${t}) ${pct}%, var(--hf-ink))`;
const HEAT = [
  { bg: 'var(--hf-surface-2)', fg: 'var(--hf-ink-3)', bd: 'var(--hf-rule)' },
  { bg: mix('--hf-sky', 10), fg: 'var(--hf-ink-2)', bd: mix('--hf-sky', 16) },
  { bg: mix('--hf-accent', 22), fg: tint('--hf-accent', 30), bd: mix('--hf-accent', 30) },
  { bg: mix('--hf-accent', 38), fg: tint('--hf-accent', 20), bd: mix('--hf-accent-2', 55) },
  { bg: mix('--hf-amber', 22), fg: tint('--hf-amber', 60), bd: mix('--hf-amber', 40) },
  { bg: mix('--hf-rose', 28), fg: tint('--hf-rose', 55), bd: mix('--hf-rose', 45) },
];
const heat = (booked: number) =>
  HEAT[
    booked > 1 ? 5 : booked > 0.9 ? 4 : booked > 0.7 ? 3 : booked > 0.45 ? 2 : booked > 0.25 ? 1 : 0
  ];

// Away is a filled dot, tentative a ring: shape, not colour, so neither reads
// as a status. Both sit in the cell's corner.
const mark = (kind: 'away' | 'tentative', right: number): CSSProperties => ({
  position: 'absolute',
  top: 2,
  right,
  width: 5,
  height: 5,
  borderRadius: 999,
  boxSizing: 'border-box',
  ...(kind === 'away' ? { background: 'currentColor' } : { border: '1.25px solid currentColor' }),
});

const weekText = (w: CapacityWeek): string => {
  if (w.standard_hours <= 0) return 'No working days';
  const parts = [`${hrs(w.free_hours)}h free of ${hrs(w.standard_hours)}h`];
  if (w.away_hours > 0) parts.push(`${hrs(w.away_hours)}h away`);
  if (w.tentative_hours > 0) parts.push(`${hrs(w.tentative_hours)}h tentative`);
  return parts.join(', ');
};

const pct = (v: number) => Math.round(v * 100);

/** One heatmap cell: the design's 22px tile, or a dashed gap with no working days. */
const HeatCell = ({
  booked,
  text,
  label,
  away,
  tentative,
}: {
  booked: number | null;
  text: string;
  label: string;
  away?: boolean;
  tentative?: boolean;
}) => {
  const c = booked == null ? null : heat(booked);
  return (
    <td style={{ padding: 0 }}>
      <div
        title={label}
        style={{
          position: 'relative',
          height: 22,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--hf-font-mono)',
          fontSize: 'var(--hf-text-2xs)',
          background: c ? c.bg : 'transparent',
          color: c ? c.fg : 'var(--hf-ink-4)',
          border: c ? `1px solid ${c.bd}` : '1px dashed var(--hf-rule-2)',
        }}
      >
        <span aria-hidden>{c ? text : '–'}</span>
        <span className="hf-sr-only">{label}</span>
        {away && <span aria-hidden style={mark('away', 2)} />}
        {tentative && <span aria-hidden style={mark('tentative', away ? 9 : 2)} />}
      </div>
    </td>
  );
};

type View = 'skills' | 'people';

const HeatTable = ({ view, people }: { view: View; people: CapacityPerson[] }) => {
  const weeks = people[0]?.weeks ?? [];
  const skills = view === 'skills' ? skillLoad(people) : [];
  const rowHead: CSSProperties = {
    textAlign: 'left',
    fontSize: 'var(--hf-text-2xs)',
    fontWeight: 'var(--hf-weight-regular)',
    color: 'var(--hf-ink-2)',
    paddingRight: 'var(--hf-space-2)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  return (
    <div className="hf-scroll" style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          minWidth: 720,
          borderCollapse: 'separate',
          borderSpacing: 3,
          margin: -3,
          tableLayout: 'fixed',
        }}
      >
        <caption className="hf-sr-only">
          {view === 'skills'
            ? `How booked the people with each skill are for the next ${weeks.length} weeks`
            : `Free hours per person for the next ${weeks.length} weeks`}
        </caption>
        <colgroup>
          <col style={{ width: view === 'skills' ? 96 : 140 }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className="hf-sr-only">
              {view === 'skills' ? 'Skill' : 'Person'}
            </th>
            {weeks.map((w, i) => (
              <th
                key={w.week_start}
                scope="col"
                style={{
                  fontFamily: 'var(--hf-font-mono)',
                  fontSize: 'var(--hf-text-2xs)',
                  fontWeight: 'var(--hf-weight-regular)',
                  color: i === 0 ? 'var(--hf-ink-2)' : 'var(--hf-ink-3)',
                  paddingBottom: 3,
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="hf-sr-only">Week of </span>
                {fmtDayKey(w.week_start)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view === 'skills'
            ? skills.map((s) => (
                <tr key={s.name}>
                  <th
                    scope="row"
                    style={rowHead}
                    title={`${s.name} · ${s.people} ${s.people === 1 ? 'person' : 'people'}`}
                  >
                    {s.name}
                  </th>
                  {s.weeks.map((w) => {
                    const t = w.tentative ?? 0;
                    return (
                      <HeatCell
                        key={w.week_start}
                        booked={w.booked}
                        text={String(pct(w.booked ?? 0))}
                        tentative={t > 0}
                        label={
                          w.booked == null
                            ? 'No working days'
                            : `${pct(w.booked)}% booked of ${hrs(w.standard_hours)}h` +
                              (t > 0 ? `, ${pct(t)}% tentative` : '')
                        }
                      />
                    );
                  })}
                </tr>
              ))
            : people.map((p) => (
                <tr key={p.profile_id}>
                  <th scope="row" style={rowHead} title={p.full_name ?? undefined}>
                    {p.full_name ?? '—'}
                  </th>
                  {p.weeks.map((w) => (
                    <HeatCell
                      key={w.week_start}
                      booked={
                        w.standard_hours > 0
                          ? (w.standard_hours - w.free_hours) / w.standard_hours
                          : null
                      }
                      text={String(Math.round(w.free_hours))}
                      away={w.away_hours > 0}
                      tentative={w.tentative_hours > 0}
                      label={weekText(w)}
                    />
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
};

const keyItem: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };
const swatch = (bg: string): CSSProperties => ({
  width: 12,
  height: 10,
  borderRadius: 2,
  background: bg,
  flexShrink: 0,
});

/** The design's key, beside Export: Low ▪▪▪▪▪ Over. */
const ScaleKey = () => (
  <span
    style={{ ...keyItem, fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}
    title="How booked: under 25%, 45%, 70%, 90%, over 100% of standard hours"
  >
    Low
    {[0.2, 0.5, 0.8, 0.95, 1.1].map((v) => (
      <span key={v} aria-hidden style={swatch(heat(v).bg)} />
    ))}
    Over
  </span>
);

/** What the marks mean, under the grid. */
const MarksKey = ({ view }: { view: View }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'var(--hf-space-1) var(--hf-space-4)',
      marginTop: 'var(--hf-space-3)',
      fontSize: 'var(--hf-text-2xs)',
      color: 'var(--hf-ink-3)',
    }}
  >
    <span>
      {view === 'skills'
        ? 'Cells: % of standard hours booked — approved leave, until projects book time ahead.'
        : 'Cells: free hours; colour: how much of the week is booked.'}
    </span>
    {view === 'people' && (
      <span style={keyItem}>
        <span aria-hidden style={{ ...mark('away', 0), position: 'static' }} /> away (approved
        leave)
      </span>
    )}
    <span style={keyItem}>
      <span aria-hidden style={{ ...mark('tentative', 0), position: 'static' }} /> tentative (leave
      waiting for approval)
    </span>
    <span style={keyItem}>
      <span
        aria-hidden
        style={{ ...swatch('transparent'), border: '1px dashed var(--hf-rule-2)' }}
      />
      no working days
    </span>
  </div>
);

/** Red from 100%, amber from 85%, the design's green below (utilisationTone). */
const utilTone = (u: number) => {
  const t = utilisationTone(u);
  return t === 'accent' ? 'success' : t;
};

const TalentCard = ({ p, util }: { p: CapacityPerson; util: number | undefined }) => {
  const from = freeFrom(p);
  const now = p.weeks[0];
  const tone = util == null ? 'neutral' : utilTone(util);
  const bar = { success: 'sage', warning: 'amber', danger: 'rose', neutral: 'sage' }[tone];
  return (
    <Card
      style={{
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar name={p.full_name ?? undefined} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 'var(--hf-weight-regular)',
              color: 'var(--hf-ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.full_name ?? '—'}
            {p.freelance && ' · ext'}
          </h3>
          <div
            style={{
              fontSize: 'var(--hf-text-2xs)',
              color: 'var(--hf-ink-3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {[p.title, p.department].filter(Boolean).join(' · ') || 'No title yet'}
          </div>
        </div>
        <span
          title={`Project hours ÷ standard hours, last ${UTIL_WEEKS} weeks`}
          style={{ flexShrink: 0 }}
        >
          <Badge tone={tone} dot>
            {util == null ? '—' : `${util}%`}
            <span className="hf-sr-only">
              {util == null ? ' no utilisation figure' : ` utilisation, last ${UTIL_WEEKS} weeks`}
            </span>
          </Badge>
        </span>
      </div>
      {p.skills.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {p.skills.map((s) => (
            <SkillLevelChip key={s.skill_id ?? s.name} name={s.name} level={s.level} />
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
          No skills rated yet
        </div>
      )}
      <div style={{ marginTop: 'auto' }}>
        <Progress value={util ?? 0} tone={bar} size={4} />
      </div>
      <div
        style={{
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Icon name="clock" size={11} /> Free from{' '}
        <Mono style={{ color: 'var(--hf-ink-2)' }}>{from ? fmtDayKey(from) : '—'}</Mono>
        {now && (
          <span style={{ marginLeft: 'auto' }} title={`This week: ${weekText(now)}`}>
            {hrs(now.free_hours)}h free this week
          </span>
        )}
      </div>
    </Card>
  );
};

type Project = { id: string; name: string; status: string; project_skill_reqs: SkillReq[] };

const SuggestMix = ({ people, onClose }: { people: CapacityPerson[]; onClose: () => void }) => {
  const [projects, setProjects] = useState<Project[] | null>(supabase ? null : []);
  const [failed, setFailed] = useState(false);
  const [pick, setPick] = useState('');
  useEffect(() => {
    if (!supabase) return;
    let live = true;
    void supabase
      .from('projects')
      .select('id, name, status, project_skill_reqs(skill_id, pct)')
      .order('name')
      .then(({ data, error }) => {
        if (!live) return;
        setFailed(!!error);
        setProjects(
          ((data as Project[] | null) ?? []).filter((p) => projectState(p) !== 'shipped'),
        );
      });
    return () => {
      live = false;
    };
  }, []);
  const project = projects?.find((p) => p.id === pick);
  const picks = project ? suggestMix(people, project.project_skill_reqs) : [];
  const muted: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
  return (
    <Dialog open onClose={onClose} title="Suggest mix" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
        <p style={{ ...muted, margin: 0 }}>
          Pick a project: people holding the skills it needs, with free hours this week, best fit
          first.
        </p>
        {failed ? (
          <p role="alert" style={{ margin: 0, color: 'var(--hf-rose-text)' }}>
            Couldn’t load projects.
          </p>
        ) : !projects ? (
          <p role="status" style={{ ...muted, margin: 0 }}>
            Loading projects…
          </p>
        ) : !projects.length ? (
          <p style={{ ...muted, margin: 0 }}>No open projects you can see.</p>
        ) : (
          <Select
            ariaLabel="Project"
            placeholder="Choose a project…"
            value={pick}
            onChange={setPick}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}
        {project &&
          (!project.project_skill_reqs.length ? (
            <p style={{ ...muted, margin: 0 }}>
              This project lists no skills it needs yet. Add them on the project page.
            </p>
          ) : !picks.length ? (
            <p style={{ ...muted, margin: 0 }}>
              Nobody with those skills has free hours this week.
            </p>
          ) : (
            <ul
              aria-label="Suggested people"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--hf-space-2)',
              }}
            >
              {picks.map((m) => (
                <li
                  key={m.person.profile_id}
                  className="hf-panel"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 'var(--hf-radius-md)',
                  }}
                >
                  <Avatar name={m.person.full_name ?? undefined} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                      {m.person.full_name ?? '—'}
                      {m.person.freelance && ' · ext'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {m.matched.map((s) => (
                        <SkillLevelChip key={s.skill_id ?? s.name} name={s.name} level={s.level} />
                      ))}
                    </div>
                  </div>
                  <Mono style={{ color: 'var(--hf-ink-2)', whiteSpace: 'nowrap' }}>
                    {hrs(m.free)}h free
                  </Mono>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </Dialog>
  );
};

const matches = (p: CapacityPerson, q: string): boolean =>
  !q ||
  [p.full_name, p.title, p.department, ...p.skills.map((s) => s.name)].some((v) =>
    v?.toLowerCase().includes(q),
  );

/** Higher is better for utilisation and billable; lower is better for bench. */
const trendOf = (delta: string | null, upIsGood: boolean): 'up' | 'down' | 'neutral' =>
  !delta || delta === '0' ? 'neutral' : delta.startsWith('+') === upIsGood ? 'up' : 'down';
const spark = (vals: (number | null)[]) => {
  const v = vals.filter((x): x is number => x != null);
  return v.length >= 2 ? v : undefined;
};

export function CapacityScreen() {
  const { perms, session, profile } = useAuth();
  const { workdays } = useOrgDates();
  const me = session?.user.id ?? profile?.id;
  const peopleFull = can(perms, 'people', 'full');
  const seesAllTime = can(perms, 'timesheets', 'full');

  const [people, setPeople] = useState<CapacityPerson[] | null>(null);
  const [trend, setTrend] = useState<CapacityTrendWeek[]>([]);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [skill, setSkill] = useState('');
  const [view, setView] = useState<View>('skills');
  const [mixOpen, setMixOpen] = useState(false);
  const [util, setUtil] = useState<Map<string, number>>(new Map());
  const [spend, setSpend] = useState<number | null>(null);

  const load = useCallback(async () => {
    const [r, t] = await Promise.all([fetchCapacity(WEEKS), fetchCapacityTrend(WEEKS)]);
    setPeople(r.data);
    setFailed(r.failed);
    setTrend(t);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // Talent's 4-week utilisation, for the people whose time this viewer may read.
  const week0 = people?.[0]?.weeks[0]?.week_start;
  useEffect(() => {
    if (!supabase || !people?.length || !week0) return;
    const sb = supabase;
    let live = true;
    const from = addDaysToKey(week0, -7 * UTIL_WEEKS);
    const to = addDaysToKey(week0, -1);
    const ids = people.map((p) => p.profile_id);
    void Promise.all([
      sb
        .from('time_allocation')
        .select('user_id, work_date, project_id, seconds')
        .in('user_id', ids)
        .gte('work_date', from)
        .lte('work_date', to),
      listHolidays(from, to),
      sb.from('profiles').select('id, joined').in('id', ids),
    ]).then(([time, hol, joined]) => {
      if (!live || time.error || hol.error) return;
      setUtil(
        utilisationByPerson(people, (time.data as AllocRow[] | null) ?? [], {
          from,
          to,
          workdays,
          holidays: new Set(hol.data.map((h) => h.date)),
          joined: new Map(
            ((joined.data as { id: string; joined: string | null }[] | null) ?? []).map((j) => [
              j.id,
              j.joined,
            ]),
          ),
          readable: (id) => seesAllTime || id === me,
        }),
      );
    });
    return () => {
      live = false;
    };
  }, [people, week0, workdays, seesAllTime, me]);

  // Freelance spend: contractors' monthly cost, people:full only (RLS agrees).
  useEffect(() => {
    if (!supabase || !peopleFull || !people) return;
    const ids = people.filter((p) => p.freelance).map((p) => p.profile_id);
    if (!ids.length) return; // ₹0, worked out at render
    let live = true;
    void supabase
      .from('employee_compensation')
      .select('profile_id, annual_ctc, currency')
      .in('profile_id', ids)
      .then(({ data, error }) => {
        if (!live) return;
        const rows = (data as { annual_ctc: number; currency: string }[] | null) ?? [];
        // Rupees only: a sum across currencies would be a made-up figure.
        setSpend(
          error || rows.some((r) => r.currency !== 'INR')
            ? null
            : Math.round(rows.reduce((a, r) => a + Number(r.annual_ctc), 0) / 12),
        );
      });
    return () => {
      live = false;
    };
  }, [people, peopleFull]);

  const skillOptions = useMemo(() => {
    const names = new Set((people ?? []).flatMap((p) => p.skills.map((s) => s.name)));
    return [
      { value: '', label: 'Any skill' },
      ...[...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n })),
    ];
  }, [people]);
  const roleOptions = useMemo(() => {
    const names = new Set(
      (people ?? []).flatMap((p) => [p.title, p.department]).filter((v): v is string => !!v),
    );
    return [
      { value: '', label: 'Any role' },
      ...[...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n })),
    ];
  }, [people]);

  const page: CSSProperties = {
    padding: '20px var(--hf-space-5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--hf-space-4)',
  };
  if (failed || !people)
    return (
      <div style={page}>
        {failed ? (
          <LoadError what="capacity" onRetry={() => void load()} />
        ) : (
          <div role="status" style={{ color: 'var(--hf-ink-3)' }}>
            Loading capacity…
          </div>
        )}
      </div>
    );

  const q = query.trim().toLowerCase();
  const shown = people.filter(
    (p) =>
      matches(p, q) &&
      (!role || p.title === role || p.department === role) &&
      (!skill || p.skills.some((s) => s.name === skill)),
  );
  const freelance = people.filter((p) => p.freelance).length;

  // Tiles: the last finished week against the one before (capacity_trend).
  const last = trend.at(-1);
  const prev = trend.at(-2);
  const u = last ? weekUtilisation(last) : null;
  const uDelta = fmtDelta(u, prev ? weekUtilisation(prev) : null);
  const b = last ? weekBillableRatio(last) : null;
  const bDelta = fmtDelta(b, prev ? weekBillableRatio(prev) : null);
  // Bench: this week's free hours; before, each finished week's (standard − away).
  const benchNow = people.reduce((a, p) => a + (p.weeks[0]?.free_hours ?? 0), 0);
  const benchWas = (w: CapacityTrendWeek) => w.standard_hours - w.away_hours;
  const benchDelta = fmtDelta(Math.round(benchNow), last ? Math.round(benchWas(last)) : null);

  return (
    <div style={page}>
      <KpiGrid>
        <KpiTile
          label="Team utilization"
          value={u}
          unit="%"
          deltaAt="head"
          delta={uDelta}
          trend={trendOf(uDelta, true)}
          tone="accent"
          data={spark(trend.map(weekUtilisation))}
        />
        <KpiTile
          label="Billable ratio"
          value={b}
          unit="%"
          deltaAt="head"
          delta={bDelta}
          trend={trendOf(bDelta, true)}
          tone="sage"
          data={spark(trend.map(weekBillableRatio))}
        />
        <KpiTile
          label="Bench hours / wk"
          value={Math.round(benchNow)}
          unit="h"
          deltaAt="head"
          delta={benchDelta}
          trend={trendOf(benchDelta, false)}
          tone="sky"
          data={spark([...trend.slice(-(WEEKS - 1)).map(benchWas), benchNow])}
        />
        {peopleFull && (
          <KpiTile
            label="Freelance spend"
            value={freelance === 0 ? fmtINR(0) : spend == null ? null : fmtINR(spend)}
            unit="/mo"
            deltaAt="head"
            tone="amber"
          />
        )}
      </KpiGrid>

      {people.length === 0 ? (
        <Card>
          <EmptyState title="No one in the plan">
            Capacity lists every active person you can see. Who you see is set in Organisation
            settings.
          </EmptyState>
        </Card>
      ) : (
        <>
          <Card style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <SectionHeader
                kicker={`${WEEKS}-week horizon`}
                title={view === 'skills' ? 'Skill capacity forecast' : 'Free hours by person'}
                actions={
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                    <SegmentedControl
                      label="Heatmap rows"
                      value={view}
                      onChange={setView}
                      options={[
                        { value: 'skills', label: 'Skills' },
                        { value: 'people', label: 'People' },
                      ]}
                    />
                    <ScaleKey />
                    {/* Exports who is shown, so a search or filter narrows the file. */}
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="download"
                      ariaLabel="Export CSV"
                      disabled={!shown.length}
                      onClick={() =>
                        downloadCsv(
                          `hyprflow-capacity-${people[0]?.weeks[0]?.week_start ?? 'weeks'}.csv`,
                          capacityCsvRows(shown),
                        )
                      }
                    >
                      Export
                    </Button>
                  </div>
                }
              />
            </div>
            {shown.length ? (
              <>
                <HeatTable view={view} people={shown} />
                <MarksKey view={view} />
              </>
            ) : (
              <EmptyState title="Nobody matches">
                Clear the search or pick another filter.
              </EmptyState>
            )}
          </Card>

          <section aria-label="Talent grid">
            <div style={{ marginBottom: 12 }}>
              <SectionHeader
                kicker={
                  shown.length === people.length
                    ? `${people.length} ${people.length === 1 ? 'person' : 'people'} · ${freelance} freelance`
                    : `${shown.length} of ${people.length} people`
                }
                title="Talent grid"
                actions={
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <TextInput
                      dense
                      type="search"
                      aria-label="Find talent"
                      placeholder="Find talent…"
                      value={query}
                      onChange={setQuery}
                      style={{ width: 220, maxWidth: '100%', padding: '4px 10px' }}
                      leading={
                        <Icon
                          name="search"
                          size={14}
                          style={{ color: 'var(--hf-ink-3)', flexShrink: 0 }}
                        />
                      }
                    />
                    <div style={{ minWidth: 130 }}>
                      <Select
                        ariaLabel="Role"
                        dense
                        value={role}
                        options={roleOptions}
                        onChange={setRole}
                      />
                    </div>
                    <div style={{ minWidth: 130 }}>
                      <Select
                        ariaLabel="Skill"
                        dense
                        value={skill}
                        options={skillOptions}
                        onChange={setSkill}
                      />
                    </div>
                    <Button variant="accent" size="sm" icon="zap" onClick={() => setMixOpen(true)}>
                      Suggest mix
                    </Button>
                  </div>
                }
              />
              <span role="status" className="hf-sr-only">
                {shown.length === people.length
                  ? `${people.length} people`
                  : `${shown.length} of ${people.length}`}
              </span>
            </div>
            {shown.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 15rem), 1fr))',
                  gap: 12,
                }}
              >
                {shown.map((p) => (
                  <TalentCard key={p.profile_id} p={p} util={util.get(p.profile_id)} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
      {mixOpen && <SuggestMix people={people} onClose={() => setMixOpen(false)} />}
    </div>
  );
}
